<template>
  <HpButton
    :variant="buttonVariant"
    @click="apply"
    :compact="compact"
    :disabled="isDisabled"
    :event-data="eventData"
    :class="$attrs.class"
  >
    <slot> {{ text }}</slot>
  </HpButton>
  <PxModalEnrollmentLocationOption
    :id="`${medicareId}-enrollment-location-option-modal`"
    :carrier-name="carrierName"
    :external-app-url="externalAppUrl"
    :plan-year="planYear!"
    :on-hp-apply="startApplication"
  />
</template>

<script lang="ts" setup>
  import { SessionStore } from '~/stores/session'
  import { ProfileStore } from '~/stores/profile'
  import { EngineStore } from '~/stores/engine'
  import { PlanType, WaypointResult } from '~/generated/api-clients-generated'
  import { csnpQualifiersToNames } from '~/utils/csnpQualifiers'
  import { ApplicationStore } from '~/stores/applications'
  import dayjs from 'dayjs'

  const props = defineProps<{
    medicareId: string
    externalAppUrl?: string | null | undefined
    carrierName: string
    planYear: number
    type: PlanType
    compact?: boolean
    secondary?: boolean
    eventData?: any
    from?: string
    disable?: boolean
    link?: boolean
  }>()

  const buttonVariant = computed(() => {
    if (props.secondary) return 'secondary'
    if (props.link) return 'minimal'
    return 'primary'
  })

  const session = SessionStore.use()
  const profile = ProfileStore.use()
  const apps = ApplicationStore.use()
  const { flag } = FeatureFlags.use()
  const { tagEvent } = SessionReplay.use()
  const { track } = Analytics.use()

  const { isHidden, isInProgress } = ApplicationStatuses.use()

  const enrollmentLocationOption = computed(
    () =>
      flag('enrollment-location-option', false).value && props.externalAppUrl && !currentApp.value
  )

  const isPreshop = computed(() => flag('is-pre-shopping', false).value)
  const showPreshopModal = computed(
    () => props.type !== PlanType.GAP && isPreshop.value && props.planYear === dayjs().year() + 1
  )

  const currentApp = computed(() =>
    apps.applications.find(
      (a) => a.medicareId === props.medicareId && a.planYear === props.planYear && !isHidden(a)
    )
  )

  const isDisabled = computed(
    () => props.disable || (currentApp.value && !isInProgress(currentApp.value))
  )

  const text = computed(() => {
    if (props.type === PlanType.GAP && !isInProgress(currentApp.value)) return 'Apply'
    if (!currentApp.value) return flag('apply-button-text', 'Apply').value
    if (isInProgress(currentApp.value)) return 'Continue Application'

    return 'Applied For'
  })

  const { show } = DialogController.use()

  const apply = async () => {
    console.log(props.medicareId, props.planYear)
    console.log(currentApp.value)

    if (props.from == 'best-plans') {
      tagEvent('AppliedFromBestPlans')
    }

    if (showPreshopModal.value) {
      show('preshop-apply-modal')
      track('PreshopAppStarted', {
        medicareId: props.medicareId,
        profileId: profile.id
      })
      if (!profile.savedPlans[props.planYear]?.includes(props.medicareId)) {
        profile.addSavedPlan(props.planYear, props.medicareId)
      }
      return
    }

    if (enrollmentLocationOption.value) {
      show(`${props.medicareId}-enrollment-location-option-modal`)
      track('EnrollmentLocationOptionModalOpened', {
        carrierName: props.carrierName
      })
      return
    }

    await startApplication()
  }

  const startApplication = async () => {
    try {
      if (!currentApp.value) {
        const engineSnapshot = EngineStore.retrieveSnapshot()

        let quote = engineSnapshot?.quotes?.find((x) => x.medicareId === props.medicareId)
        let details = engineSnapshot?.plans?.find((x) => x.medicareId === props.medicareId)

        if (!quote || !details) {
          console.error(`failed to find quote for ${props.medicareId}`)
          return
        }

        // cloning
        quote = { ...quote }
        details = { ...details }

        if (!_isNil(engineSnapshot)) {
          engineSnapshot.plans = [details]
          engineSnapshot.quotes = [quote]
        }

        const layoutConfig = flag<{ pages: string[] }>('app-layout-config').value

        session.pendingApplicationData = {
          //@ts-ignore
          profileId: null, // set in PageApplyRouter AFTER sign-up/in
          medicareId: quote.medicareId,
          planName: quote.name,
          planLetter: quote.planLetter,
          planType: quote.type,
          planExternalId: quote.externalId,
          //@ts-ignore
          planYear: details.year,
          policyType: details.policyType,
          //@ts-ignore
          carrierId: details.carrierId,
          //@ts-ignore
          carrierKey: details.carrierKey,
          carrierName: details.carrierName,
          brandName: details.brandName,
          brandId: details.brandId,
          logoKey: details.logoKey,
          monthlyPremium: details.premium,
          sobUrl: details.externalResources?.summaryOfBenefitsUrl,
          eocUrl: details.externalResources?.evidenceOfCoverageUrl,
          snpType: details.snpType,
          flow: details.appFlow,
          destination: details.appDestination,
          naic: details.naic,
          engineData: engineSnapshot as { [key: string]: any },
          ext: [
            {
              key: 'layout_config',
              value: JSON.stringify(layoutConfig),
              valueType: 'json'
            },
            // the plan's chronic conditions follow the application since the quote isn't always
            // available to the schema. stored as names so oxcart and px read the same value
            {
              key: 'csnp_qualifiers',
              value: JSON.stringify(csnpQualifiersToNames(details.csnpQualifiers)),
              valueType: 'json'
            }
          ]
        }
      } else {
        session.currentApplicationId = currentApp!.value.id!
      }

      console.log(session.pendingApplicationData)

      const autopilot = WaypointAutoPilot.use(WaypointResult.GoTo_Apply)
      await autopilot.engage()
    } catch (err) {
      console.error(err)
    }
  }
</script>

<style scoped></style>
